'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { normalizeNANPNationalDigits, toE164NANP } from '@/utils/phone'

/**
 * Strips non-digits and normalises known autofill patterns to a bare NANP national number.
 *
 * Why this exists: browsers with autoComplete="tel" paste the full international
 * number (e.g. "+12125551234"). We unwrap the country code so the result is always
 * the 10-digit national number the rest of the system expects.
 *
 * Edge cases handled:
 *   "+12125551234"  → "2125551234"  (autoComplete="tel" autofill)
 *   "12125551234"   → "2125551234"  (country code without +)
 *   "447911123456"  → "447911123456" (non-US paste — left alone so Zod shows an error)
 */
export function normalizePhoneDigits(raw: string): string {
  return normalizeNANPNationalDigits(raw)
}

/**
 * Convert friendly NANP input to E.164 (+1XXXXXXXXXX).
 * Kept for backwards-compatible imports; delegates to the shared phone utility.
 */
export function toE164FromNational(national: string): string | null {
  return toE164NANP(national)
}

/** Format 10 digits as (XXX) XXX-XXXX. Returns value unchanged if not 10 digits. */
function formatNational(digits: string): string {
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return digits
}

interface PhoneInputProps extends Omit<
  React.ComponentProps<'input'>,
  'type' | 'value' | 'onChange' | 'autoComplete'
> {
  /** Raw 10-digit national number (no +1, no formatting). */
  value?: string
  /** Called with the raw 10-digit string on every change. */
  onChange?: (value: string) => void
}

/**
 * US/Canada phone input with a fixed +1 country-code adornment.
 *
 * Key behaviours:
 * - autoComplete="tel-national" → browsers autofill only the 10-digit national
 *   portion, avoiding the +1 collision that breaks plain type="tel" inputs
 * - Paste/autofill of "+12125551234" or "12125551234" is silently normalised to
 *   "2125551234" via normalizePhoneDigits
 * - Displays (XXX) XXX-XXXX on blur; raw digits on focus so editing is clean
 * - onChange always emits raw 10 digits — callers convert to E.164 for API calls
 *
 * Display strategy: the inner <input> is semi-uncontrolled — its DOM value is
 * manipulated directly on focus/blur to avoid the cursor-jump bug that occurs
 * when a formatted string is fed back into a React-controlled input mid-keystroke.
 * The parent's `value` prop is the source of truth for the data model only.
 */
const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    { value = '', onChange, className, onFocus, onBlur, disabled, ...props },
    ref
  ) => {
    const innerRef = React.useRef<HTMLInputElement>(null)

    // Merge the forwarded ref with our internal ref so both callers and this
    // component can access the DOM node.
    const setRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref)
          (ref as React.MutableRefObject<HTMLInputElement | null>).current =
            node
      },
      [ref]
    )

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (innerRef.current) innerRef.current.value = value
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (innerRef.current) innerRef.current.value = formatNational(value)
      onBlur?.(e)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(normalizePhoneDigits(e.target.value).slice(0, 10))
    }

    // Keep the formatted display in sync when the parent value changes while
    // the input is not focused (e.g. form reset, seeding from user.phone).
    React.useEffect(() => {
      if (innerRef.current && document.activeElement !== innerRef.current) {
        innerRef.current.value = formatNational(value)
      }
    }, [value])

    return (
      <div
        className={cn(
          'flex h-10 sm:h-8 w-full rounded-md border border-input bg-transparent shadow-sm transition-colors duration-150',
          'focus-within:ring-2 focus-within:ring-ring',
          disabled && 'cursor-not-allowed opacity-50',
          className
        )}
      >
        <span
          className="flex items-center pl-3 pr-1 text-base sm:text-sm text-muted-foreground select-none shrink-0"
          aria-hidden="true"
        >
          +1
        </span>

        <input
          {...props}
          ref={setRef}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          defaultValue={formatNational(value)}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          maxLength={14}
          className={cn(
            'flex-1 min-w-0 bg-transparent px-2 py-2 sm:py-1.5 text-base sm:text-sm',
            'placeholder:text-muted-foreground/60',
            'focus-visible:outline-none',
            'disabled:cursor-not-allowed',
            'touch-manipulation'
          )}
        />
      </div>
    )
  }
)
PhoneInput.displayName = 'PhoneInput'

export { PhoneInput }
